import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * Floating "Install Madreterra TV" prompt.
 * - Shows when the browser fires `beforeinstallprompt` (Chrome/Edge/Android).
 * - On iOS Safari (no event), shows an instructional hint after a short delay,
 *   unless the app is already running standalone.
 * - Dismissals are remembered for 14 days.
 */
const DISMISS_KEY = "mt_pwa_dismiss_until";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return until && Date.now() < until;
  } catch (_e) {
    return false;
  }
}

function rememberDismiss(days = 14) {
  try {
    localStorage.setItem(
      DISMISS_KEY,
      String(Date.now() + days * 24 * 60 * 60 * 1000)
    );
  } catch (_e) {
    /* ignore */
  }
}

export const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isDismissed()) return;
    // Android is handled by the richer AndroidInstallGuide bottom-sheet.
    if (/Android/i.test(window.navigator.userAgent || "")) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setVisible(false);
      setIosHint(false);
      rememberDismiss(365);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS handled by dedicated IosInstallGuide component
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice.outcome !== "accepted") rememberDismiss(14);
    } catch (_e) {
      /* ignore */
    }
    setVisible(false);
    setDeferred(null);
  };

  const dismiss = () => {
    rememberDismiss(14);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 mt-rise"
      data-testid="install-prompt"
    >
      <div
        className="rounded-2xl border p-4 sm:p-5 flex gap-4 items-start"
        style={{
          background: "rgba(20,17,13,0.94)",
          borderColor: "rgba(212,160,91,0.35)",
          backdropFilter: "blur(14px)",
          boxShadow: "0 24px 60px -10px rgba(0,0,0,0.7)",
        }}
      >
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            background: "rgba(212,160,91,0.18)",
            color: "var(--mt-amber-bright)",
            border: "1px solid rgba(212,160,91,0.35)",
          }}
        >
          <Download size={20} strokeWidth={2.2} />
        </div>

        <div className="flex-1 min-w-0">
          <div
            className="font-display text-lg leading-tight"
            style={{ color: "var(--mt-cream)" }}
          >
            Installa MADRETERRA radio
          </div>
          <p
            className="text-xs sm:text-sm mt-1 leading-snug"
            style={{ color: "var(--mt-text-muted)" }}
          >
            {iosHint ? (
              <>
                Tocca{" "}
                <span style={{ color: "var(--mt-cream)" }}>Condividi</span> →{" "}
                <span style={{ color: "var(--mt-cream)" }}>
                  Aggiungi alla schermata Home
                </span>{" "}
                per aggiungerla come app.
              </>
            ) : (
              "Aggiungila alla schermata Home: parte a tutto schermo, senza barra del browser."
            )}
          </p>

          {!iosHint && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={install}
                data-testid="install-btn"
                className="mt-btn inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: "var(--mt-amber)", color: "#1a1410" }}
              >
                <Download size={13} strokeWidth={2.4} />
                Installa
              </button>
              <button
                onClick={dismiss}
                data-testid="install-dismiss"
                className="mt-btn text-xs px-2.5 py-1.5 rounded-full"
                style={{ color: "var(--mt-text-muted)" }}
              >
                Più tardi
              </button>
            </div>
          )}
        </div>

        <button
          onClick={dismiss}
          aria-label="Chiudi"
          data-testid="install-close"
          className="mt-btn -mr-1 -mt-1 w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0"
          style={{
            color: "var(--mt-text-muted)",
            background: "rgba(245,239,228,0.06)",
          }}
        >
          <X size={14} strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
};

export default InstallPrompt;
